import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

interface AnimatedCardProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'elevated' | 'gradient';
  onClick?: () => void;
  hover?: boolean;
}

interface AnimatedCardHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface AnimatedCardTitleProps {
  children: React.ReactNode;
  className?: string;
}

interface AnimatedCardContentProps {
  children: React.ReactNode;
  className?: string;
}

export const AnimatedCard: React.FC<AnimatedCardProps> = ({
  children,
  className = '',
  variant = 'default',
  onClick,
  hover = true
}) => {
  const shouldReduceMotion = useReducedMotion();

  const baseClasses = 'bg-white rounded-lg border border-gray-200';

  const variantClasses = {
    default: 'shadow-sm',
    elevated: 'shadow-md',
    gradient: 'shadow-md overflow-hidden'
  };

  const cardVariants = shouldReduceMotion || !hover
    ? undefined
    : {
        rest: {
          scale: 1,
          boxShadow: variant === 'elevated'
            ? '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
            : '0 1px 2px 0 rgb(0 0 0 / 0.05)'
        },
        hover: {
          scale: 1.02,
          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
          transition: { duration: 0.15, ease: 'easeInOut' }
        }
      };

  const MotionComponent = motion.div;

  return (
    <MotionComponent
      className={`${baseClasses} ${variantClasses[variant]} ${className} ${onClick ? 'cursor-pointer' : ''}`}
      variants={cardVariants}
      initial="rest"
      whileHover="hover"
      onClick={onClick}
    >
      {variant === 'gradient' && (
        <div className="h-2 bg-gradient-to-r from-purple-600 to-pink-600" />
      )}
      {children}
    </MotionComponent>
  );
};

export const AnimatedCardHeader: React.FC<AnimatedCardHeaderProps> = ({
  children,
  className = ''
}) => {
  return (
    <div className={`p-6 pb-0 ${className}`}>
      {children}
    </div>
  );
};

export const AnimatedCardTitle: React.FC<AnimatedCardTitleProps> = ({
  children,
  className = ''
}) => {
  return (
    <h3 className={`text-lg font-medium text-gray-900 ${className}`}>
      {children}
    </h3>
  );
};

export const AnimatedCardContent: React.FC<AnimatedCardContentProps> = ({
  children,
  className = ''
}) => {
  return (
    <div className={`p-6 ${className}`}>
      {children}
    </div>
  );
};
